import { Event, EventInput, EventParticipant, EventParticipantInput, EventMemberConflict } from '../../model/event/Event';
import { RecurrenceInput } from '../../model/appointment/Appointment';
import i18n from '../../i18n';

const API_URL = process.env.REACT_APP_CALENDAR_SERVICE_BASE_URL || '';

const EVENT_FIELDS = `
    id
    businessId
    serviceId
    title
    description
    startTime
    endTime
    status
    capacity
    isPublic
    defaultPrice
    depositRequired
    depositAmount
    location
    notes
    cancellationReason
    seriesId
    recurrenceFreq
    teamMemberIds
    totalPrice
    alreadyPaid
    paymentStatus
    updatedOn
    participants {
        id
        eventId
        clientId
        customerName
        customerPhone
        customerEmail
        price
        paymentStatus
        notes
    }
`;

async function gqlPost<T>(token: string, query: string, variables?: object): Promise<T> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'Accept-Language': i18n.language || 'pt',
        },
        body: JSON.stringify({ query, variables }),
    });
    const json = await res.json();
    if (json.errors) {
        throw new Error(json.errors.map((e: { message: string }) => e.message).join(', '));
    }
    return json.data as T;
}

export async function getEventsByBusinessAndRange(
    businessId: string, start: string, end: string, token: string,
): Promise<Event[]> {
    const data = await gqlPost<{ getEventsByBusinessAndRange: Event[] | null }>(
        token,
        `query($businessId: ID!, $start: String!, $end: String!) {
            getEventsByBusinessAndRange(businessId: $businessId, start: $start, end: $end) { ${EVENT_FIELDS} }
        }`,
        { businessId, start, end },
    );
    return data.getEventsByBusinessAndRange ?? [];
}

export async function getEventById(id: string, token: string): Promise<Event | null> {
    const data = await gqlPost<{ getEventById: Event | null }>(
        token,
        `query($id: ID!) { getEventById(id: $id) { ${EVENT_FIELDS} } }`,
        { id },
    );
    return data.getEventById;
}

export async function createEvent(input: EventInput, token: string): Promise<Event> {
    const data = await gqlPost<{ createEvent: Event }>(
        token,
        `mutation($input: EventInput!) { createEvent(input: $input) { ${EVENT_FIELDS} } }`,
        { input },
    );
    return data.createEvent;
}

export async function createRecurringEvent(
    input: EventInput, recurrence: RecurrenceInput, token: string,
): Promise<Event[]> {
    const data = await gqlPost<{ createRecurringEvent: Event[] }>(
        token,
        `mutation($input: EventInput!, $recurrence: RecurrenceInput!) {
            createRecurringEvent(input: $input, recurrence: $recurrence) { ${EVENT_FIELDS} }
        }`,
        { input, recurrence },
    );
    return data.createRecurringEvent;
}

export async function updateEvent(input: EventInput, token: string): Promise<Event> {
    const data = await gqlPost<{ updateEvent: Event }>(
        token,
        `mutation($input: EventInput!) { updateEvent(input: $input) { ${EVENT_FIELDS} } }`,
        { input },
    );
    return data.updateEvent;
}

export async function cancelEvent(
    id: string, reason: string | null, wholeSeries: boolean, token: string,
): Promise<boolean> {
    const data = await gqlPost<{ cancelEvent: boolean }>(
        token,
        `mutation($id: ID!, $reason: String, $wholeSeries: Boolean) {
            cancelEvent(id: $id, reason: $reason, wholeSeries: $wholeSeries)
        }`,
        { id, reason, wholeSeries },
    );
    return data.cancelEvent;
}

export async function addEventParticipant(
    eventId: string, input: EventParticipantInput, token: string,
): Promise<EventParticipant> {
    const data = await gqlPost<{ addEventParticipant: EventParticipant }>(
        token,
        `mutation($eventId: ID!, $input: EventParticipantInput!) {
            addEventParticipant(eventId: $eventId, input: $input) {
                id eventId clientId customerName customerPhone customerEmail price paymentStatus notes
            }
        }`,
        { eventId, input },
    );
    return data.addEventParticipant;
}

export async function removeEventParticipant(participantId: string, token: string): Promise<boolean> {
    const data = await gqlPost<{ removeEventParticipant: boolean }>(
        token,
        `mutation($participantId: ID!) { removeEventParticipant(participantId: $participantId) }`,
        { participantId },
    );
    return data.removeEventParticipant;
}

export async function checkEventConflicts(
    businessId: string, teamMemberIds: string[], startTime: string, endTime: string,
    excludeEventId: string | null, token: string,
): Promise<EventMemberConflict[]> {
    const data = await gqlPost<{ checkEventConflicts: EventMemberConflict[] }>(
        token,
        `query($businessId: ID!, $teamMemberIds: [ID!]!, $startTime: String!, $endTime: String!, $excludeEventId: ID) {
            checkEventConflicts(businessId: $businessId, teamMemberIds: $teamMemberIds, startTime: $startTime, endTime: $endTime, excludeEventId: $excludeEventId) {
                teamMemberId hasConflict description conflictEventId
            }
        }`,
        { businessId, teamMemberIds, startTime, endTime, excludeEventId },
    );
    return data.checkEventConflicts ?? [];
}
